import type { Theme } from '@mui/material';
import { Box, Typography, styled, useTheme } from '@mui/material';
import { Building } from 'iconsax-react';
import type { FC, ReactNode } from 'react';

import Enums from '@core/types/api/Enums';
import type { EntityType, GlobalSearchItem, GlobalSearchResponse } from '@core/types/search';
import { MAX_RESULTS_PER_GROUP } from '@web/constants/app';

import { ENTITY_CONFIGS } from './entityConfig';
import type { EntitySearchTab } from './useEntitySearch';

const ResultRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '12px',
  cursor: 'pointer',
  gap: 8,
  borderRadius: 8,
  '&:hover': {
    backgroundColor: theme.palette.SlabGray.Table,
  },
}));

const EntityName = styled(Typography)(({ theme }) => ({
  flex: 1,
  fontSize: 14,
  color: theme.palette.SlabGray[900],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const StatusBadge = styled(Typography)({
  fontSize: 12,
  padding: '2px 8px',
  borderRadius: 4,
  whiteSpace: 'nowrap',
});

const ContextBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  height: 24,
  padding: '0 8px',
  borderRadius: 8,
  backgroundColor: theme.palette.SlabGray.Stroke,
  fontSize: 14,
  color: theme.palette.SlabGray[800],
  whiteSpace: 'nowrap',
}));

const SectionHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 16px 4px',
});

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  color: theme.palette.SlabGray[600],
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}));

const getStatusBadgeColors = (theme: Theme, statusName: string): { bg: string; text: string } => {
  // Quote/project statuses are tenant-configurable strings with no generated enum;
  // only ActivityStatus has enum values in generated-enums.tsx.
  switch (statusName) {
    case 'Needs Review':
      return { bg: theme.palette.SlabYellow[200], text: theme.palette.SlabBrown[600] };
    case 'Pending':
    case 'Sent':
      return { bg: theme.palette.SlabYellow[100], text: theme.palette.SlabYellow[800] };
    case 'Won':
    case 'Approved':
    case Enums.ActivityStatus.Completed:
      return { bg: theme.palette.SlabGreen[100], text: theme.palette.SlabGreen[600] };
    case Enums.ActivityStatus.Planned:
      return { bg: theme.palette.SlabBlue.IceLight, text: theme.palette.SlabTurquoise[600] };
    case Enums.ActivityStatus.Canceled:
      return { bg: theme.palette.SlabRed[100], text: theme.palette.SlabRed[600] };
    case 'Draft':
    default:
      return { bg: theme.palette.SlabGray.Stroke, text: theme.palette.SlabGray[800] };
  }
};

type Props = {
  results: GlobalSearchResponse;
  entityTypes: EntityType[];
  activeTab: EntitySearchTab;
  onSelect: (type: EntityType, item: GlobalSearchItem) => void;
  onViewAll?: (type: EntityType) => void;
};

export const EntitySearchResults: FC<Props> = ({
  results,
  entityTypes,
  activeTab,
  onSelect,
  onViewAll,
}) => {
  const theme = useTheme();

  const renderGroup = (type: EntityType): ReactNode => {
    const group = results.result[type];
    if (!group || group.data.length === 0) return null;

    const config = ENTITY_CONFIGS[type];
    const items = group.data.slice(0, MAX_RESULTS_PER_GROUP);

    return (
      <Box key={type}>
        <SectionHeader>
          <SectionLabel>{config.pluralLabel}</SectionLabel>
          {onViewAll !== undefined && group.totalCount > MAX_RESULTS_PER_GROUP && (
            <Typography
              sx={{
                fontSize: 12,
                color: theme.palette.SlabIndigo[500],
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={() => onViewAll(type)}
            >
              View all {config.pluralLabel.toLowerCase()}
            </Typography>
          )}
        </SectionHeader>
        {items.map((item) => {
          const { id } = item;
          const name = config.getName(item);
          const status = config.getStatus(item);
          const context = config.getContext(item);
          const { Icon } = config;
          const badgeColors = status !== null ? getStatusBadgeColors(theme, status) : null;

          return (
            <ResultRow
              key={id}
              role='button'
              tabIndex={0}
              onClick={() => onSelect(type, item)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(type, item);
                }
              }}
            >
              <Icon size={24} variant='TwoTone' color={theme.palette.SlabGray[600]} />
              <EntityName>{name}</EntityName>
              {context !== null && context !== '' && (
                <ContextBadge>
                  <Building size={16} variant='TwoTone' color={theme.palette.SlabGray[600]} />
                  {context}
                </ContextBadge>
              )}
              {status !== null && badgeColors !== null && (
                <StatusBadge sx={{ backgroundColor: badgeColors.bg, color: badgeColors.text }}>
                  {status}
                </StatusBadge>
              )}
            </ResultRow>
          );
        })}
      </Box>
    );
  };

  const typesToRender = activeTab === 'all' ? entityTypes : [activeTab];
  const hasResults = typesToRender.some((type) => (results.result[type]?.data.length ?? 0) > 0);

  if (!hasResults) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ color: theme.palette.SlabGray[600], fontSize: 14 }}>
          No results found
        </Typography>
      </Box>
    );
  }

  return <>{typesToRender.map(renderGroup)}</>;
};
